import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { mandateRegistration } from "../../../api/bseApi";

const ACCOUNT_TYPES = [
  { value: "SB", label: "Savings (SB)" },
  { value: "CA", label: "Current (CA)" },
];
const FREQUENCIES = [
  { value: "MONTHLY", label: "MONTHLY" },
  { value: "DAILY", label: "DAILY" },
  { value: "AS PRESENTED", label: "AS PRESENTED" },
];

/** IFSC: 11 chars, format XXXX0XXXXXX */
const IFSC_REGEX = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;

function formatDateForBse(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * MandateRegistrationScreen - BSE StAR MF Mandate Registration (e-NACH / OTM / XSIP)
 * Opens before SIP / XSIP registration
 * Receives: ClientCode, Registered bank details (if available)
 */
export default function MandateRegistrationScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const state = location.state;
  const [context, setContext] = useState(null);
  const [form, setForm] = useState({
    accountNo: "",
    ifscCode: "",
    bankName: "",
    accountType: "SB",
    mandateAmount: "",
    fromDate: "",
    toDate: "",
    frequency: "MONTHLY",
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const clientCode = state?.clientCode;
    if (clientCode) {
      setContext({
        clientCode,
        bankDetails: state?.bankDetails,
      });
      if (state?.bankDetails) {
        setForm((prev) => ({
          ...prev,
          accountNo: state.bankDetails.accountNo || prev.accountNo,
          ifscCode: state.bankDetails.ifscCode || prev.ifscCode,
          bankName: state.bankDetails.bankName || prev.bankName,
        }));
      }
      setFetching(false);
      return;
    }
    if (userId) {
      const fetchUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          if (!ucc || String(ucc).trim() === "" || String(ucc) === "Reject") {
            Swal.fire("Error", "ClientCode (UCC) not available. Complete UCC creation first.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({
            clientCode: String(ucc).trim(),
            bankDetails: state?.bankDetails,
          });
          if (state?.bankDetails) {
            setForm((prev) => ({
              ...prev,
              accountNo: state.bankDetails.accountNo || prev.accountNo,
              ifscCode: state.bankDetails.ifscCode || prev.ifscCode,
              bankName: state.bankDetails.bankName || prev.bankName,
            }));
          }
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
      return;
    }
    Swal.fire("Error", "Missing ClientCode. Open from SIP registration or User Listing.", "error").then(() => navigate("/user"));
    setFetching(false);
  }, [location.state, userId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "ifscCode") setForm((prev) => ({ ...prev, ifscCode: (value || "").toUpperCase() }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    if (!form.accountNo?.trim()) err.accountNo = "Account Number is mandatory";
    if (!form.ifscCode?.trim()) err.ifscCode = "IFSC Code is mandatory";
    else if (!IFSC_REGEX.test(form.ifscCode.trim())) err.ifscCode = "Invalid IFSC format (e.g. HDFC0001234)";
    if (!form.bankName?.trim()) err.bankName = "Bank Name is mandatory";
    const amt = parseFloat(form.mandateAmount);
    if (!form.mandateAmount || isNaN(amt) || amt <= 0) err.mandateAmount = "Mandate Amount must be greater than 0";
    const fromDate = form.fromDate ? new Date(form.fromDate) : null;
    const toDate = form.toDate ? new Date(form.toDate) : null;
    if (!form.fromDate) err.fromDate = "Mandate Start Date is mandatory";
    if (!form.toDate) err.toDate = "Mandate End Date is mandatory";
    if (fromDate && toDate && fromDate >= toDate) err.toDate = "End Date must be after Start Date";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const returnUrl = `${window.location.origin}/mandate/return`;
      const payload = {
        clientCode: context.clientCode,
        accountNo: form.accountNo.trim(),
        ifscCode: form.ifscCode.trim(),
        bankName: form.bankName.trim(),
        accountType: form.accountType,
        mandateAmount: parseFloat(form.mandateAmount),
        fromDate: formatDateForBse(form.fromDate),
        toDate: formatDateForBse(form.toDate),
        frequency: form.frequency,
        returnUrl,
      };

      const res = await mandateRegistration(payload);

      if (res.data?.status === 1) {
        const mandateUrl = res.data?.data?.mandateUrl;
        if (mandateUrl) {
          window.location.href = mandateUrl;
          return;
        }
        Swal.fire("Success", "Mandate registration initiated.", "success").then(() => navigate("/user"));
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "Mandate registration failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const msg =
        err.response?.data?.bseMessage ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Mandate registration failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetching || !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">Mandate Registration</li>
              </ol>
            </nav>
            <h5>Mandate Registration (e-NACH / OTM / XSIP)</h5>
            <p className="text-muted mb-0">Client Code: {context.clientCode}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Bank & Mandate Details
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Bank Account Number (AccountNo) <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.accountNo ? "is-invalid" : ""}`}
                    name="accountNo"
                    value={form.accountNo}
                    onChange={handleChange}
                    placeholder="e.g. 123456789012"
                  />
                  {errors.accountNo && <div className="invalid-feedback">{errors.accountNo}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">IFSC Code (IFSCCode) <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.ifscCode ? "is-invalid" : ""}`}
                    name="ifscCode"
                    value={form.ifscCode}
                    onChange={handleChange}
                    placeholder="e.g. HDFC0001234"
                    maxLength={11}
                  />
                  {errors.ifscCode && <div className="invalid-feedback">{errors.ifscCode}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Bank Name (BankName) <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.bankName ? "is-invalid" : ""}`}
                    name="bankName"
                    value={form.bankName}
                    onChange={handleChange}
                    placeholder="e.g. HDFC Bank"
                  />
                  {errors.bankName && <div className="invalid-feedback">{errors.bankName}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Account Type (AccountType)</label>
                  <select
                    className="form-select"
                    name="accountType"
                    value={form.accountType}
                    onChange={handleChange}
                  >
                    {ACCOUNT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Mandate Amount (Max Debit Limit) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${errors.mandateAmount ? "is-invalid" : ""}`}
                    name="mandateAmount"
                    value={form.mandateAmount}
                    onChange={handleChange}
                    min="1"
                    step="1"
                    placeholder="e.g. 50000"
                  />
                  {errors.mandateAmount && <div className="invalid-feedback">{errors.mandateAmount}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Frequency</label>
                  <select
                    className="form-select"
                    name="frequency"
                    value={form.frequency}
                    onChange={handleChange}
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Mandate Start Date (FromDate) <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-control ${errors.fromDate ? "is-invalid" : ""}`}
                    name="fromDate"
                    value={form.fromDate}
                    onChange={handleChange}
                  />
                  {errors.fromDate && <div className="invalid-feedback">{errors.fromDate}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Mandate End Date (ToDate) <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-control ${errors.toDate ? "is-invalid" : ""}`}
                    name="toDate"
                    value={form.toDate}
                    onChange={handleChange}
                  />
                  {errors.toDate && <div className="invalid-feedback">{errors.toDate}</div>}
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Submitting...
                    </>
                  ) : (
                    "Register Mandate"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
